import { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import { useSearchParams } from "react-router-dom";
import { Box, Card, Stepper, Step, StepLabel, Typography, Fade, Alert } from "@mui/material";
import { useFormik, FormikProvider } from "formik";
import Page from "src/components/Page";
import AboutYouStep from "../signup/AboutYouStep";
import YourNeedsStep from "../signup/YourNeedsStep";
import PlanSelectionStep from "./PlanSelectionStep";
import ReviewStep from "./ReviewStep";
import CheckoutPage from "./CheckoutPage";
import { onboardingValidationSchema } from "src/util/validationSchemas";
import { onboardingService } from "src/services";
import {
  loadOnboardingData,
  saveOnboardingData,
  clearOnboardingData,
} from "src/util/onboardingStorage";

const RootStyle = styled(Page)(({ theme }) => ({
  position: "relative",
  minHeight: "100vh",
  backgroundColor: "#f6f7ff",
  [theme.breakpoints.up("md")]: {
    display: "flex",
  },
}));

// Verify Email is intentionally excluded here — by the time the user
// reaches /subscription, the backend has already verified the email and
// redirected them with ?status=success&userId=.
const STEPS = ["About You", "Your Needs", "Plan Selection", "Review & Pay", "Checkout"];
const TOTAL_STEPS = STEPS.length;

export default function PlanCheckoutFlow() {
  const [searchParams] = useSearchParams();
  const urlUserId = searchParams.get("userId");
  const isVerified = searchParams.get("status") === "success";

  // Only trust locally-persisted onboarding data when we arrived via a
  // valid confirmation redirect. If the token was invalid, the backend
  // redirects the user elsewhere before they ever reach this page — but
  // we guard here regardless, and wipe storage otherwise.
  const persisted = useMemo(() => (isVerified ? loadOnboardingData() : null), [isVerified]);

  useEffect(() => {
    if (!isVerified) {
      clearOnboardingData();
    }
  }, [isVerified]);

  // Prefer the userId from the URL (authoritative, from the backend
  // redirect) but fall back to whatever was cached locally.
  const userId = urlUserId ?? persisted?.userId;

  // Steps 0 (About You) and 1 (Your Needs) are already completed by the
  // time the user lands here — start on Plan Selection.
  const [activeStep, setActiveStep] = useState(2);
  const [plan, setPlan] = useState(null);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState(null);
  const [onboardingData, setOnboardingData] = useState(null);

  const formik = useFormik({
    initialValues: {
      firstName: persisted?.firstName ?? "",
      lastName: persisted?.lastName ?? "",
      email: persisted?.email ?? "",
      country: persisted?.country ?? "",
      companyName: persisted?.companyName ?? "",
      useCase: persisted?.useCase ?? "home",
      chargingSetup: persisted?.chargingSetup ?? "",
      acceptedTerms: persisted?.acceptedTerms ?? true,
      chargerCount: persisted?.chargerCount ?? "",
    },
    validationSchema: onboardingValidationSchema,
    onSubmit: () => {},
  });

  // Keep sessionStorage in sync if the user edits About You / Your Needs
  // while navigating back from later steps, so the values stay
  // pre-filled on repeated back-and-forth.
  useEffect(() => {
    if (isVerified) {
      saveOnboardingData({ ...persisted, ...formik.values, userId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values, isVerified]);

  // Derived from live form values instead of a mock/prop.
  const account = {
    name: `${formik.values.firstName} ${formik.values.lastName}`.trim(),
    email: formik.values.email,
    company: formik.values.companyName,
  };

  const validateStepFields = async (fields) => {
    try {
      await onboardingValidationSchema.pick(fields).validate(formik.values, {
        abortEarly: false,
      });
      return true;
    } catch (err) {
      const errors = {};
      const touchedFields = {};
      (err.inner ?? []).forEach((e) => {
        errors[e.path] = e.message;
        touchedFields[e.path] = true;
      });
      formik.setErrors({ ...formik.errors, ...errors });
      formik.setTouched({ ...formik.touched, ...touchedFields });
      return false;
    }
  };

  const handleAboutYouNext = async () => {
    if (await validateStepFields(["firstName", "lastName", "email", "companyName", "country"])) {
      setActiveStep(1);
    }
  };

  // No email resend / re-registration here — email is already verified.
  // "Continuing" from Your Needs just advances to Plan Selection.
  const handleYourNeedsContinue = async () => {
    if (!formik.values.acceptedTerms) {
      formik.setFieldError("acceptedTerms", "Please accept the terms to continue");
      formik.setFieldTouched("acceptedTerms", true);
      return;
    }
    if (await validateStepFields(["useCase", "chargingSetup", "acceptedTerms"])) {
      setActiveStep(2);
    }
  };

  const handlePlanNext = (payload) => {
    setPlan(payload);
    setActiveStep(3);
  };

  const handleReviewConfirm = async () => {
    if (!plan) return;

    setIsSubmittingReview(true);
    setReviewError(null);

    try {
      const payload = {
        amount: plan.total_cost,
        userId,
      };

      const response = await onboardingService.initiatePaymentOnboarding(payload);
      setOnboardingData(response);
      setActiveStep(4);
    } catch (err) {
      setReviewError(err?.response?.data?.message || "Couldn't start payment. Please try again.");
    } finally {
      setIsSubmittingReview(false);
    }
  };

  if (activeStep === 4 && plan) {
    return (
      <Page title="Checkout | Smart-CMS">
        <CheckoutPage
          plan={plan}
          account={account}
          onboarding={onboardingData?.data}
          onBack={() => setActiveStep(3)}
        />
      </Page>
    );
  }

  const cardMaxWidth = activeStep === 2 ? "100%" : 720;

  return (
    <RootStyle title="Plan Selection | Smart-CMS">
      <Box
        sx={{
          width: "100%",
          minHeight: "100vh",
          display: "grid",
          placeItems: "center",
          padding: { xs: "40px 16px", md: "60px 32px" },
        }}
      >
        <Card sx={{ width: "100%", maxWidth: cardMaxWidth, p: { xs: 3, md: 4 }, borderRadius: "15px" }}>
          <Box sx={{ mb: 4 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {STEPS.map((label, index) => (
                <Step key={label} completed={index < activeStep}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <Typography
              variant="caption"
              color="text.secondary"
              align="center"
              sx={{ display: "block", mt: 1 }}
            >
              Step {activeStep + 1} of {TOTAL_STEPS}
            </Typography>
          </Box>

          {reviewError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReviewError(null)}>
              {reviewError}
            </Alert>
          )}

          <Fade in timeout={300} key={activeStep}>
            <Box>
              {activeStep === 0 && (
                <FormikProvider value={formik}>
                  <AboutYouStep onNext={handleAboutYouNext} />
                </FormikProvider>
              )}
              {activeStep === 1 && (
                <FormikProvider value={formik}>
                  <YourNeedsStep
                    onBack={() => setActiveStep(0)}
                    onSubmit={handleYourNeedsContinue}
                    submitLabel="Continue"
                  />
                </FormikProvider>
              )}
              {activeStep === 2 && (
                <PlanSelectionStep onNext={handlePlanNext} onBack={() => setActiveStep(1)} />
              )}
              {activeStep === 3 && plan && (
                <ReviewStep
                  account={account}
                  plan={plan}
                  isSubmitting={isSubmittingReview}
                  onBack={() => setActiveStep(2)}
                  onConfirm={handleReviewConfirm}
                />
              )}
            </Box>
          </Fade>
        </Card>
      </Box>
    </RootStyle>
  );
}